/**
 * Counting (Inventory) Workflow Simulator
 * Simulates the complete inventory counting workflow using real API calls
 */

import { getSharedRateLimiter } from '../services/RateLimiter';
import { VirtualOperator } from '../services/VirtualOperator';
import { BatchSerialHelper } from '../services/BatchSerialHelper';
import { AuthService } from '../../../../../services/authService';
import { SimulatorConfig } from '../types';

// API base URL - use environment variable for production deployments
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

// Authenticated fetch helper using AuthService
const authenticatedFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  return AuthService.authenticatedFetch(url, options);
};

export interface CountingWorkflowResult {
  success: boolean;
  batchId?: string;
  itemsCounted: number;
  locationsCounted: number;
  error?: string;
  duration: number;
}

export class CountingWorkflow {
  private config: SimulatorConfig;
  private operator: VirtualOperator;
  private rateLimiter = getSharedRateLimiter();

  constructor(config: SimulatorConfig, operator: VirtualOperator) {
    this.config = config;
    this.operator = operator;
  }

  /**
   * Execute one complete counting workflow cycle
   */
  async execute(): Promise<CountingWorkflowResult> {
    const startTime = Date.now();
    let batchId: string | undefined;
    let itemsCounted = 0;
    let locationsCounted = 0;

    try {
      // Step 1: Create a new inventory counting session
      this.operator.startTask('counting', 'Oppretter tellesession');
      await this.rateLimiter.throttle();

      // Use operator ID + milliseconds to ensure unique BatchID across multiple simulators
      batchId = `SIM-COUNT-${this.operator.getId()}-${Date.now()}`;

      await this.createCountingSession(batchId);

      // Step 2: Get items/bins to count (select random bins or items)
      this.operator.startTask('counting', 'Henter lokasjoner å telle');
      await this.rateLimiter.throttle();

      const binsToCont = await this.getBinsForCounting();

      if (binsToCont.length === 0) {
        // Complete the session even if no bins to count
        await this.completeCountingSession(batchId);
        return {
          success: true,
          batchId,
          itemsCounted: 0,
          locationsCounted: 0,
          duration: Date.now() - startTime,
          error: 'Ingen lokasjoner å telle'
        };
      }

      // Step 3: Select a subset of bins to count (max 5 for simulation)
      const selectedBins = binsToCont.slice(0, Math.min(5, binsToCont.length));

      // Step 4: Count each bin
      const batchSerialEnabled = this.config.batchSerialConfig?.enabled ?? false;
      const batchHelper = batchSerialEnabled ? new BatchSerialHelper() : null;

      for (const bin of selectedBins) {
        this.operator.startTask('counting', `Teller lokasjon ${bin.binCode || bin.locationName}`);

        // Simulate walking to location
        await this.delay(this.config.timing.walkTimeMs);

        // Simulate counting
        await this.delay(this.config.timing.countTimeMs);

        // Fetch batch/serial info if enabled
        let batchSerialFields: Record<string, string> = {};
        if (batchSerialEnabled && batchHelper) {
          const itemType = bin.itemType ?? 0;
          const binCode = bin.binCode || bin.locationName;
          if (BatchSerialHelper.needsBatchTracking(itemType)) {
            const batches = await batchHelper.getBatchesAtBin(bin.itemNo, this.config.warehouseId, binCode);
            if (batches.length > 0) {
              batchSerialFields = {
                itemBatchNo: batches[0].batchNo,
                ...(batches[0].expiryDate ? { itemBatchExpDate: batches[0].expiryDate } : {})
              };
            }
          } else if (BatchSerialHelper.needsSerialTracking(itemType)) {
            const serials = await batchHelper.getSerialNumbersAtBin(bin.itemNo, this.config.warehouseId, binCode);
            if (serials.length > 0) {
              batchSerialFields = { itemSerialNo: serials[0].serialNo };
            }
          }
        }

        // Add counting transaction
        await this.rateLimiter.throttle();
        await this.addCountingTransaction(batchId, {
          itemNo: bin.itemNo,
          locationName: bin.binCode || bin.locationName,
          quantity: bin.quantity || Math.floor(Math.random() * 100) + 1,
          warehouse: bin.warehouseID || this.config.warehouseId,
          ...batchSerialFields
        });

        itemsCounted++;
        locationsCounted++;
      }

      // Step 5: Complete the counting session
      this.operator.startTask('counting', `Fullfører tellesession ${batchId}`);
      await this.rateLimiter.throttle();
      await this.completeCountingSession(batchId);

      return {
        success: true,
        batchId,
        itemsCounted,
        locationsCounted,
        duration: Date.now() - startTime
      };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Ukjent feil';
      console.error(`[CountingWorkflow] Error:`, errorMessage);

      return {
        success: false,
        batchId,
        itemsCounted,
        locationsCounted,
        duration: Date.now() - startTime,
        error: errorMessage
      };
    }
  }

  /**
   * Create a new counting session
   */
  private async createCountingSession(batchId: string): Promise<any> {
    const now = new Date();

    const response = await authenticatedFetch(
      `${API_BASE_URL}/stocktrans`,
      {
        method: 'POST',
        body: JSON.stringify({
          batchID: batchId,
          operatorID: this.operator.getId(),
          orderDate: now.toISOString(),
          description1: `Simulator telling ${now.toLocaleDateString('no-NO')} ${now.toLocaleTimeString('no-NO')}`,
          orderStatus: 20, // Under arbeid
          orderType: 1, // Counting
          warehouse: this.config.warehouseId,
          startWorkDateTime: now.toISOString()
        })
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to create counting session: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get bins/locations for counting
   */
  private async getBinsForCounting(): Promise<any[]> {
    try {
      // Get all item bins and filter by warehouse
      const response = await authenticatedFetch(
        `${API_BASE_URL}/data-inspection/itembins`
      );

      if (response.ok) {
        const allBins = await response.json();
        // Filter to bins in this warehouse with positive quantities
        return allBins.filter((b: any) =>
          b.quantity > 0 &&
          (b.warehouseID === this.config.warehouseId || b.warehouse === this.config.warehouseId)
        );
      }
    } catch (error) {
      console.warn('[CountingWorkflow] Could not get item bins:', error);
    }

    // Fallback: return empty array
    return [];
  }

  /**
   * Add a counting transaction
   */
  private async addCountingTransaction(batchId: string, data: {
    itemNo: string;
    locationName: string;
    quantity: number;
    warehouse: string;
    itemBatchNo?: string;
    itemBatchExpDate?: string;
    itemSerialNo?: string;
  }): Promise<any> {
    const encodedBatchId = encodeURIComponent(batchId);

    const body: Record<string, any> = {
      batchID: batchId,
      itemNo: data.itemNo,
      locationName: data.locationName,
      quantity: data.quantity,
      warehouse: data.warehouse,
      operatorID: this.operator.getId(),
      lineType: 1, // Counting
      transType: 100, // TransType 100 = Telling (for transaction history)
      lineStatus: 0,
      transDt: new Date().toISOString()
    };

    if (data.itemBatchNo) body.itemBatchNo = data.itemBatchNo;
    if (data.itemBatchExpDate) body.itemBatchExpDate = data.itemBatchExpDate;
    if (data.itemSerialNo) body.itemSerialNo = data.itemSerialNo;

    const response = await authenticatedFetch(
      `${API_BASE_URL}/stocktrans/${encodedBatchId}/transactions`,
      {
        method: 'POST',
        body: JSON.stringify(body)
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to add counting transaction: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Complete the counting session
   */
  private async completeCountingSession(batchId: string): Promise<void> {
    const encodedBatchId = encodeURIComponent(batchId);

    const response = await authenticatedFetch(
      `${API_BASE_URL}/stocktrans/${encodedBatchId}/complete`,
      {
        method: 'POST',
        body: JSON.stringify({})
      }
    );

    if (!response.ok) {
      // Non-critical - session may still be valid
      console.warn(`[CountingWorkflow] Could not complete counting session: ${response.statusText}`);
    }
  }

  /**
   * Delay helper
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

export default CountingWorkflow;
